import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Select,
  Tabs,
  Tag,
  Modal,
  Alert,
  Spin,
  Card
} from 'antd';
import {
  RotateCw,
  Plus,
  LayoutDashboard,
  TrendingUp,
  Bell,
  Filter,
  X
} from 'lucide-react';
import { plantApiGet } from '../../utils/plant_api';
import SensorCard from './SensorCard';
import SensorDataChart from './SensorDataChart';
import SensorAlertPanel from './SensorAlertPanel';
import { defaultColors } from '@/themes/antd-theme';
import { PageRail } from '@/components/PageChrome';

const { Option } = Select;

const SensorMonitoringDashboard = () => {
  const [sensors, setSensors] = useState([]);
  const [filteredSensors, setFilteredSensors] = useState([]);
  const [sensorTypes, setSensorTypes] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [equipmentFilter, setEquipmentFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [sensors, statusFilter, typeFilter, equipmentFilter, searchQuery]);

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchSensors(),
        fetchSensorTypes(),
        fetchEquipment()
      ]);
    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load sensor data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSensors = async () => {
    try {
      const response = await plantApiGet('/api/sensors');
      setSensors(response.data || []);
    } catch (err) {
      console.error('Error fetching sensors:', err);
      throw err;
    }
  };

  const fetchSensorTypes = async () => {
    try {
      const response = await plantApiGet('/api/sensors/types');
      setSensorTypes(response.data || []);
    } catch (err) {
      console.error('Error fetching sensor types:', err);
    }
  };

  const fetchEquipment = async () => {
    try {
      const response = await plantApiGet('/api/sensors/equipment');
      setEquipment(response.data || []);
    } catch (err) {
      console.error('Error fetching equipment:', err);
    }
  };

  const applyFilters = () => {
    let filtered = [...sensors];

    // Status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'ALL') {
      filtered = filtered.filter(s => s.sensor_type === parseInt(typeFilter));
    }

    // Equipment filter
    if (equipmentFilter !== 'ALL') {
      filtered = filtered.filter(s => s.equipment === parseInt(equipmentFilter));
    }

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s =>
        s.sensor_name.toLowerCase().includes(query) ||
        s.sensor_code.toLowerCase().includes(query) ||
        (s.sensor_type_details?.name || '').toLowerCase().includes(query)
      );
    }

    setFilteredSensors(filtered);
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
    fetchSensors();
  };

  const handleViewDetails = (sensor) => {
    setSelectedSensor(sensor);
    setDetailsDialogOpen(true);
  };

  const clearFilters = () => {
    setStatusFilter('ALL');
    setTypeFilter('ALL');
    setEquipmentFilter('ALL');
    setSearchQuery('');
  };

  const activeSensorCount = sensors.filter(s => s.status === 'ACTIVE').length;
  const faultySensorCount = sensors.filter(s => s.status === 'FAULTY').length;
  const calibrationDueCount = sensors.filter(s => s.is_calibration_due).length;

  return (
    <div className="max-w-7xl mx-auto mt-8 mb-8 px-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold m-0">
          Sensor Monitoring Dashboard
        </h1>
      </div>
      <PageRail items={[{ key: 'refresh', icon: RotateCw, label: 'Refresh data', onClick: handleRefresh }]} />

      {error && (
        <Alert
          message={error}
          type="error"
          closable
          onClose={() => setError(null)}
          className="mb-6"
        />
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4" style={{ backgroundColor: defaultColors.palette.blue[50] }}>
          <h4 className="text-3xl font-bold m-0 mb-1" style={{ color: defaultColors.info }}>
            {sensors.length}
          </h4>
          <p className="text-sm text-gray-600 m-0">
            Total Sensors
          </p>
        </Card>
        <Card className="p-4" style={{ backgroundColor: defaultColors.palette.green[50] }}>
          <h4 className="text-3xl font-bold m-0 mb-1" style={{ color: defaultColors.success }}>
            {activeSensorCount}
          </h4>
          <p className="text-sm text-gray-600 m-0">
            Active Sensors
          </p>
        </Card>
        <Card className="p-4" style={{ backgroundColor: defaultColors.palette.red[50] }}>
          <h4 className="text-3xl font-bold m-0 mb-1" style={{ color: defaultColors.error }}>
            {faultySensorCount}
          </h4>
          <p className="text-sm text-gray-600 m-0">
            Faulty Sensors
          </p>
        </Card>
        <Card className="p-4" style={{ backgroundColor: defaultColors.palette.orange[50] }}>
          <h4 className="text-3xl font-bold m-0 mb-1" style={{ color: defaultColors.warning }}>
            {calibrationDueCount}
          </h4>
          <p className="text-sm text-gray-600 m-0">
            Calibration Due
          </p>
        </Card>
      </div>

      {/* Tabs */}
      <Card className="mb-6">
        <Tabs
          activeKey={String(activeTab)}
          onChange={(key) => setActiveTab(Number(key))}
          items={[
            {
              key: '0',
              label: (
                <span className="flex items-center gap-2">
                  <LayoutDashboard size={16} />
                  All Sensors
                </span>
              ),
            },
            {
              key: '1',
              label: (
                <span className="flex items-center gap-2">
                  <TrendingUp size={16} />
                  Sensor Trends
                </span>
              ),
            },
            {
              key: '2',
              label: (
                <span className="flex items-center gap-2">
                  <Bell size={16} />
                  Alerts
                </span>
              ),
            },
          ]}
        />
      </Card>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Filters */}
          <Card className="p-4 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter size={20} />
              <h6 className="text-base font-semibold m-0">Filters</h6>
              <Button size="small" onClick={clearFilters} className="ml-auto">
                Clear All
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Input
                  size="middle"
                  placeholder="Sensor name or code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  allowClear
                />
              </div>
              <div>
                <Select
                  style={{ width: '100%' }}
                  value={statusFilter}
                  onChange={(value) => setStatusFilter(value)}
                  placeholder="Status"
                >
                  <Option value="ALL">All</Option>
                  <Option value="ACTIVE">Active</Option>
                  <Option value="INACTIVE">Inactive</Option>
                  <Option value="MAINTENANCE">Maintenance</Option>
                  <Option value="FAULTY">Faulty</Option>
                </Select>
              </div>
              <div>
                <Select
                  style={{ width: '100%' }}
                  value={typeFilter}
                  onChange={(value) => setTypeFilter(value)}
                  placeholder="Sensor Type"
                >
                  <Option value="ALL">All</Option>
                  {sensorTypes.map(type => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </div>
              <div>
                <Select
                  style={{ width: '100%' }}
                  value={equipmentFilter}
                  onChange={(value) => setEquipmentFilter(value)}
                  placeholder="Equipment"
                >
                  <Option value="ALL">All</Option>
                  {equipment.map(eq => (
                    <Option key={eq.id} value={eq.id}>
                      {eq.equipment_name}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </Card>

          {/* Sensor Grid */}
          {loading ? (
            <div className="flex justify-center py-16">
              <Spin size="large" />
            </div>
          ) : filteredSensors.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-600 m-0">
                No sensors found
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredSensors.map(sensor => (
                <div key={sensor.id}>
                  <SensorCard
                    sensor={sensor}
                    latestReading={sensor.latest_reading}
                    onViewDetails={handleViewDetails}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <SensorDataChart sensor={selectedSensor} />
          </div>
          <div>
            <Card className="p-4">
              <h6 className="text-base font-semibold mb-4">
                Select Sensor
              </h6>
              <div className="flex flex-wrap gap-2">
                {sensors.map(sensor => (
                  <Tag
                    key={sensor.id}
                    color={selectedSensor?.id === sensor.id ? defaultColors.primary : 'default'}
                    onClick={() => setSelectedSensor(sensor)}
                    className="cursor-pointer"
                  >
                    {sensor.sensor_name}
                  </Tag>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 2 && (
        <SensorAlertPanel refreshTrigger={refreshTrigger} />
      )}

      {/* Sensor Details Modal */}
      <Modal
        title="Sensor Details"
        open={detailsDialogOpen}
        onCancel={() => setDetailsDialogOpen(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailsDialogOpen(false)}>
            Close
          </Button>
        ]}
      >
        {selectedSensor && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Sensor Code</p>
                <p className="mb-3">{selectedSensor.sensor_code}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Sensor Name</p>
                <p className="mb-3">{selectedSensor.sensor_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Type</p>
                <p className="mb-3">{selectedSensor.sensor_type_details?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Category</p>
                <p className="mb-3">{selectedSensor.sensor_type_details?.category}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <Tag color={selectedSensor.status === 'ACTIVE' ? 'success' : 'default'}>
                  {selectedSensor.status}
                </Tag>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Equipment</p>
                <p className="mb-3">{selectedSensor.equipment_details?.equipment_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Min Threshold</p>
                <p className="mb-3">
                  {selectedSensor.min_threshold || 'N/A'} {selectedSensor.sensor_type_details?.unit_of_measurement}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Max Threshold</p>
                <p className="mb-3">
                  {selectedSensor.max_threshold || 'N/A'} {selectedSensor.sensor_type_details?.unit_of_measurement}
                </p>
              </div>
              {selectedSensor.location && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-600 mb-1">Location</p>
                  <p className="mb-3">{selectedSensor.location}</p>
                </div>
              )}
              {selectedSensor.description && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-600 mb-1">Description</p>
                  <p className="mb-3">{selectedSensor.description}</p>
                </div>
              )}
            </div>

            <div className="mt-6">
              <SensorDataChart sensor={selectedSensor} />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default SensorMonitoringDashboard;
